import React, { memo, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import { APIsetMemoryReaction } from '../../api/Memory';

import { isUserLoggedIn } from '../../helpers/auth';

import type { Memory } from '../../types/memory';
import type { Reaction } from '../../types/reaction';

import { useStore } from '../../store';

import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js';

interface MemoryReactionsProps {
  memory: Memory;
  readOnly?: boolean;
}

const MemoryReactions: React.FC<MemoryReactionsProps> = ({
  memory,
  readOnly,
}) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);

  if (!memory.allow_reactions) {
    return null;
  }

  const availableReactions = useStore((state) => state.settings?.reactions) || [];

  const { mutate: updateReaction, isPending } = useMutation({
    mutationFn: async ({ memoryId, reactionId }: { memoryId: string; reactionId: number }): Promise<Memory> => {
      const response = await APIsetMemoryReaction(memoryId, reactionId);
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Memory) => {
      setReactions(response.reactions);
    },
  });

  useEffect(() => {
    setReactions(memory.reactions);
  }, [memory.reactions]);

  const getReactionById = (reactionId: number): Reaction | undefined => {
    return availableReactions.find((reaction) => reaction.id === reactionId);
  }

  const updating = isPending;

  const handleReactionChange = (reactionId: number) => {
    let newReaction = null;
    const reaction = reactions.find((r) => r.id === reactionId);
    if (!reaction) {
      newReaction = getReactionById(reactionId);
      if (newReaction) {
        newReaction.users_count = null;
        newReaction.chosen_by_user = true;
      }
    }
    let newReactions = newReaction ? [...reactions, { ...newReaction, users_count: null, chosen_by_user: true }] : reactions;
    newReactions = newReactions.map((reaction: Reaction) => {
      if (reaction.chosen_by_user && reaction.users_count) {
        reaction.users_count -= 1;
        reaction.chosen_by_user = false;
      } else if (reaction.id === reactionId) {
        if (reaction.users_count === null) {
          reaction.chosen_by_user = true;
        } else {
          reaction.chosen_by_user = !reaction.chosen_by_user;
        }
        if (reaction.chosen_by_user) {
          if (reaction.users_count === null) {
            reaction.users_count = 1;
          } else {
            reaction.users_count += 1;
          }
        }
      } else {
        reaction.chosen_by_user = false;
      }
      return reaction;
    }).filter((reaction) => reaction.users_count === null || reaction.users_count > 0);
    setReactions(newReactions);

    updateReaction({ memoryId: memory.id, reactionId });
  }

  const reactionChangeListener = (event: CustomEvent) => {
    const reactionId = event.detail.item.value;
    handleReactionChange(parseInt(reactionId));
  };

  useEffect(() => {
    const initializeDropdown = async () => {
      await customElements.whenDefined('wa-button');
      const dropdown = document.getElementById(`memory-reaction-dropdown-${memory.id}`) as HTMLElement | null;
      if (dropdown) {
        dropdown.addEventListener('wa-select', reactionChangeListener as EventListener);
      }
    };

    initializeDropdown();

    return () => {
      const dropdown = document.getElementById(`memory-reaction-dropdown-${memory.id}`) as HTMLElement | null;
      if (dropdown) {
        dropdown.removeEventListener('wa-select', reactionChangeListener as EventListener);
      }
    }
  }, []);

  return (
    <>
      <hr />
      <div className={classnames('memory-reactions', 'wa-cluster', { 'memory-reactions--read-only': readOnly || !isUserLoggedIn() })}>
        {reactions.map((reaction) => (
          <wa-tag
            key={reaction.id}
            className={classnames('memory-reaction', { 'memory-reaction--selected': reaction.chosen_by_user, 'memory-reaction--disabled': updating })}
            onClick={(e) => { e.stopPropagation(); if (!updating) handleReactionChange(reaction.id); }}
          >
            <i className={`fa-solid fa-${reaction.icon}`}></i>
            {reaction.name} · {reaction.users_count}
          </wa-tag>
        ))}
        {reactions.length === 0 && (
          <span className="memory-reactions-no-reactions">
            {readOnly ? 'No reactions yet' : 'Be the first to react to this memory!'}
          </span>
        )}
        {!readOnly && (
          <wa-dropdown id={`memory-reaction-dropdown-${memory.id}`} placement="bottom" className="memory-reaction-dropdown">
            <wa-button appearance="outlined" size="xs" className="memory-reaction-add" slot="trigger" pill onClick={(e) => e.stopPropagation()} disabled={updating}>
              <wa-icon name="plus" label="Add reaction"></wa-icon>
              React
            </wa-button>
            {isUserLoggedIn() ? (
              availableReactions.map((reaction) => (
                <wa-dropdown-item
                  key={reaction.id}
                  className={classnames('memory-reaction-option', { 'memory-reaction-option--selected': reactions.some(r => r.id === reaction.id && r.chosen_by_user) })}
                  disabled={updating}
                  onClick={(e) => e.stopPropagation()}
                  value={`${reaction.id}`}
                >
                  <i className={`fa-solid fa-${reaction.icon}`} slot="icon"></i>
                  {reaction.name}
                </wa-dropdown-item>
              ))
            ) : (
              <div className="memory-reactions-login-prompt">
                Please <Link to="/login" search={{ redirect: location.href }}>sign in</Link> to react
              </div>
            )}
          </wa-dropdown>
        )}
      </div>
    </>
  );
}

export default MemoryReactions;
